import React, { useState, useEffect } from 'react';
import usePermissions from '../../hooks/usePermissions';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tankCleaningService from '../../services/TankCleaningService.js';
import tankService from '../../services/TankService.js';
import userService from '../../services/UserService.js';
import { ClipboardCheck, ArrowLeft, Calendar, ChevronDown, ChevronUp} from "lucide-react";
import { useAuth } from '../../context/AuthContext.js'; 
import locationService from '../../services/LocationService.js';
import { shouldLockInspectorInEdit } from '../../utils/roleBasedUIHelpers.js';

const EditTankCleaning = () => {
  const [exteriorOpen, setExteriorOpen] = useState(false);
  const [interiorOpen, setInteriorOpen] = useState(false);
  const [valvesOpen, setValvesOpen] = useState(false);
  const { cleaningId } = useParams();
  const [formData, setFormData] = useState({
    client_id: '',
    tank_id: '',
    date_of_survey: '',
    previous_cargo: '',
    location_id: '',
    internal: '',
    remarks: '',
    remarks_2: '',
    published: false,
    send_to_dm: false,
    inspected_by_id: '',
    
    tank_no: '',
    tank_type: '',
    manufacture_date: '',
    imo_class: '',
    un_number: '',
    maximum_gross_weight: '',
    tare_weight: '',
    capacity_ltr: '',
    csc: '',
    cleaning_corporation: '',
    depot_name: '',
    top_discharge: false,
    syphon_tube: false,
    led: '',
    ned: '',
    
    exterior: {
      frame_tank_walkways_free: true,
      manlid_valve_compartments_free: true,
      serial_numbers_legible: true,
      cargo_labels_removed: true
    },
    
    interior: {
      entry_made_by_surveyor: true,
      free_from_odour: true,
      clean_free_from_cargo: true,
      free_from_corrosion: true,
      dry: true
    },
    
    valves_fittings: {
      valves: true,
      manlid_seal: true,
      dip_pipe: true,
      spillage: true,
      gas_free_entry_permit: true
    }
  });

  const [clients, setClients] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [useExistingTank, setUseExistingTank] = useState(false);
  const { user } = useAuth();
  const {
    isSystemAdmin,
    isAdmin,
    isDocumentationManager,
    isSurveyInspector,
    isAccountant,
  } = usePermissions();
  const canEditPublishFields = isSystemAdmin || isAdmin || isDocumentationManager;
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchInspectors();
    fetchLocations();
    fetchCleaning();
  }, [cleaningId]);

  useEffect(() => {
    if (useExistingTank && formData.client_id) {
      fetchClientTanks(formData.client_id);
    } else if (!useExistingTank) {
      setTanks([]);
    }
  }, [formData.client_id, useExistingTank]);

  useEffect(() => {
    if (useExistingTank && formData.tank_id) {
      loadTankData(formData.tank_id);
    }
  }, [formData.tank_id, useExistingTank]);

  const fetchClients = async () => {
    try {
      // Change from clientService.getAll to userService.getClients
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchClientTanks = async (clientId) => {
    try {
      const response = await tankService.getByClientId(clientId);
      setTanks(response.data || []);
    } catch (err) {
      Alert.error('Failed to load tanks');
      console.error(err);
    }
  };

  const fetchInspectors = async () => {
    try {
      // Get users with Survey Inspector role (role_id = 4)
      const response = await userService.getByRole(4);
      setInspectors(response.data || []);
    } catch (err) {
      console.error('Failed to load inspectors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  const loadTankData = async (tankId) => {
    try {
      const response = await tankService.getById(tankId);
      const tank = response.data;
      
      setFormData(prev => ({
        ...prev,
        tank_no: tank.tank_no || '',
        tank_type: tank.tank_type || '',
        manufacture_date: tank.manufacture_date || '',
        imo_class: tank.imo_class || '',
        un_number: tank.un_number || '',
        maximum_gross_weight: tank.maximum_gross_weight || '',
        tare_weight: tank.tare_weight || '',
        capacity_ltr: tank.capacity_ltr || '',
        csc: tank.csc || '',
        cleaning_corporation: tank.cleaning_corporation || '',
        depot_name: tank.depot_name || '',
        top_discharge: tank.top_discharge === 'yes',
        syphon_tube: tank.syphon_tube === 'yes',
        led: tank.led || '',
        ned: tank.ned || ''
      }));
    } catch (err) {
      Alert.error('Failed to load tank data');
      console.error(err);
    }
  };

  const handleUseExistingTankToggle = (e) => {
    const checked = e.target.checked;
    setUseExistingTank(checked);

    if (checked) {
      if (formData.client_id) {
        fetchClientTanks(formData.client_id);
      }
    } else {
      setTanks([]);
      setFormData(prev => ({
        ...prev,
        tank_id: '',
        tank_no: '',
        tank_type: '',
        manufacture_date: '',
        imo_class: '',
        un_number: '',
        maximum_gross_weight: '',
        tare_weight: '',
        capacity_ltr: '',
        csc: '',
        cleaning_corporation: '',
        depot_name: '',
        top_discharge: false,
        syphon_tube: false,
        led: '',
        ned: ''
      }));
    }
  };

  const fetchCleaning = async () => {
    try {
      const response = await tankCleaningService.getById(cleaningId);
      const cleaning = response.data;
      
      setFormData({
        client_id: cleaning.client_id,
        tank_id: cleaning.tank_id,
        date_of_survey: cleaning.date_of_survey,
        previous_cargo: cleaning.previous_cargo || '',
        internal: cleaning.internal || '',
        remarks: cleaning.remarks || '',
        location_id: cleaning.location_id || '',
        remarks_2: cleaning.remarks_2 || '',
        published: cleaning.published,
        send_to_dm: cleaning.send_to_dm || false,
        inspected_by_id: cleaning.inspected_by_id,
        
        tank_no: cleaning.tank_no || '',
        tank_type: cleaning.tank_type || '',
        manufacture_date: cleaning.manufacture_date || '',
        imo_class: cleaning.imo_class || '',
        un_number: cleaning.un_number || '',
        maximum_gross_weight: cleaning.maximum_gross_weight || '',
        tare_weight: cleaning.tare_weight || '',
        capacity_ltr: cleaning.capacity_ltr || '',
        csc: cleaning.csc || '',
        cleaning_corporation: cleaning.cleaning_corporation || '',
        depot_name: cleaning.depot_name || '',
        top_discharge: cleaning.top_discharge === 'yes',
        syphon_tube: cleaning.syphon_tube === 'yes',
        led: cleaning.led || '',
        ned: cleaning.ned || '',
        
        exterior: cleaning.exterior || {
          frame_tank_walkways_free: true,
          manlid_valve_compartments_free: true,
          serial_numbers_legible: true,
          cargo_labels_removed: true
        },
        
        interior: cleaning.interior || {
          entry_made_by_surveyor: true,
          free_from_odour: true,
          clean_free_from_cargo: true,
          free_from_corrosion: true,
          dry: true
        },
        
        valves_fittings: cleaning.valves_fittings || {
          valves: true,
          manlid_seal: true,
          dip_pipe: true,
          spillage: true,
          gas_free_entry_permit: true
        }
      });

      // If cleaning has a tank_id, enable the existing tank toggle
      if (cleaning.tank_id) {
        setUseExistingTank(true);
        if (cleaning.client_id) {
          fetchClientTanks(cleaning.client_id);
        }
      }
    } catch (err) {
      Alert.error('Failed to load cleaning data');
      console.error(err);
      navigate('/tank-cleanings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCheckboxGroupChange = (group, field) => {
    setFormData(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: !prev[group][field]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanedData = { ...formData };
      
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && !['exterior', 'interior', 'valves_fittings'].includes(key)) {
          cleanedData[key] = null;
        }
      });

      await tankCleaningService.update(cleaningId, cleanedData);
      Alert.success('Tank cleaning record updated successfully!');
      setTimeout(() => navigate('/tank-cleanings'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update tank cleaning record');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading cleaning data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" /> Edit Tank Cleaning
                    </h1>
                  </div>
                  <button
                              onClick={() => navigate('/tank-cleanings')}
                              className="
                                relative overflow-hidden
                                group flex items-center justify-center gap-2
                                px-3 sm:px-3 py-2.5 sm:py-2
                                text-xs sm:text-sm
                                rounded-lg
                                border border-buttonBg
                                bg-buttonBg text-buttonTxt
                                font-medium whitespace-nowrap

                                transition-colors duration-300 ease-out
                                hover:bg-transparent hover:text-buttonBg
                              "
                            >
                              {/* COLLAPSING BACKGROUND */}
                              <span
                                className="
                                  absolute inset-0
                                  bg-buttonBg
                                  scale-x-100
                                  group-hover:scale-x-0
                                  origin-center
                                  transition-transform duration-300 ease-out
                                  z-0
                                "
                              />

                              {/* ICON */}
                              <ArrowLeft
                                className="
                                  w-4 h-4 relative z-10
                                  transition-transform duration-300 ease-out
                                  group-hover:scale-90
                                "
                              />

                              <span className="relative z-10">Back to Tank Cleaning</span>
                            </button>
                </div>
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Client <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                          disabled
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.name} - {client.organization_name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Client cannot be changed</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <input
                            type="checkbox"
                            id="use_existing_tank"
                            checked={useExistingTank}
                            onChange={handleUseExistingTankToggle}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                            disabled={!formData.client_id}
                          />
                          <label htmlFor="use_existing_tank" className="text-xs sm:text-sm font-medium text-secondary">
                            Use Existing Tank
                          </label>
                        </div>
                        {useExistingTank && (
                          <select
                            name="tank_id"
                            value={formData.tank_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required={useExistingTank}
                            disabled={!formData.client_id}
                          >
                            <option value="">Select Tank</option>
                            {tanks.map((tank) => (
                              <option key={tank.id} value={tank.id}>
                                {tank.tank_no || `Tank ${tank.id}`}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Survey <span className="text-danger font-bold">*</span>
                        </label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="date_of_survey"
                            value={formData.date_of_survey}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="location_id"
                          value={formData.location_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.city}, {location.state} ({location.city_code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Inspector <span className="text-danger font-bold">*</span>
                          {isSurveyInspector && <span className="text-blue-600 ml-2">(Locked)</span>}
                        </label>
                        <select
                          name="inspected_by_id"
                          value={formData.inspected_by_id}
                          onChange={handleChange}
                          disabled={isSurveyInspector}
                          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base ${isSurveyInspector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          required
                        >
                          <option value="">Select Inspector</option>
                          {inspectors.map((inspector) => (
                            <option key={inspector.user_id} value={inspector.user_id}>
                              {inspector.name}{isSurveyInspector && inspector.user_id === user?.user_id ? ' (You)' : ''} - {inspector.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Previous Cargo</label>
                        <input
                          type="text"
                          name="previous_cargo"
                          value={formData.previous_cargo}
                          onChange={handleChange}
                          placeholder="Enter previous cargo"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      {/* Role-based checkboxes for Publish to Client and Send to DM */}
                      {(() => {
                        const showPublishCheckbox = canEditPublishFields;
                        const showSendToDMCheckbox = true;
                        const sendToDMDisabled = false;
                        const publishDisabled = !canEditPublishFields;
                        return (
                          <>
                            {showPublishCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="published"
                                  name="published"
                                  checked={formData.published}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={publishDisabled}
                                />
                                <label htmlFor="published" className="text-xs sm:text-sm font-medium text-secondary">
                                  Publish to Client
                                </label>
                              </div>
                            )}
                            {showSendToDMCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="send_to_dm"
                                  name="send_to_dm"
                                  checked={formData.send_to_dm}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                  disabled={sendToDMDisabled}
                                />
                                <label htmlFor="send_to_dm" className="text-xs sm:text-sm font-medium text-secondary">
                                  Send to DM
                                </label>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Section 2: Tank Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Tank Details (From Selected Tank)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank No</label>
                        <input
                          type="text"
                          name="tank_no"
                          value={formData.tank_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank Type</label>
                        <input
                          type="text"
                          name="tank_type"
                          value={formData.tank_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manufacture Date</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="manufacture_date"
                            value={formData.manufacture_date || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">IMO Class</label>
                        <input
                          type="text"
                          name="imo_class"
                          value={formData.imo_class}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">UN Number</label>
                        <input
                          type="text"
                          name="un_number"
                          value={formData.un_number}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">MGW (kg)</label>
                        <input
                          type="text"
                          name="maximum_gross_weight"
                          value={formData.maximum_gross_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tare Weight (kg)</label>
                        <input
                          type="text"
                          name="tare_weight"
                          value={formData.tare_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Capacity (Ltr)</label>
                        <input
                          type="text"
                          name="capacity_ltr"
                          value={formData.capacity_ltr}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC</label>
                        <input
                          type="text"
                          name="csc"
                          value={formData.csc}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Cleaning Corp</label>
                        <input
                          type="text"
                          name="cleaning_corporation"
                          value={formData.cleaning_corporation}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Depot Name</label>
                        <input
                          type="text"
                          name="depot_name"
                          value={formData.depot_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">LED</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="led"
                            value={formData.led || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">NED</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="ned"
                            value={formData.ned || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-8">
                        <input
                          type="checkbox"
                          id="top_discharge"
                          name="top_discharge"
                          checked={formData.top_discharge}
                          onChange={handleChange}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                        />
                        <label htmlFor="top_discharge" className="text-xs sm:text-sm font-medium text-secondary">
                          Top Discharge
                        </label>
                      </div>

                      <div className="flex items-center gap-2 mt-8">
                        <input
                          type="checkbox"
                          id="syphon_tube"
                          name="syphon_tube"
                          checked={formData.syphon_tube}
                          onChange={handleChange}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                        />
                        <label htmlFor="syphon_tube" className="text-xs sm:text-sm font-medium text-secondary">
                          Syphon Tube
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Internal Description */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Internal Description</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Internal</label>
                        <textarea
                          name="internal"
                          value={formData.internal}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remarks</label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Exterior Checklist */}
                  <div className="border border-gray-200">

                    {/* Header */}
                    <button
                      type="button"
                      onClick={() => setExteriorOpen(!exteriorOpen)}
                      className="w-full flex justify-between items-center px-3 py-2 sm:px-4 sm:py-3 bg-gray-50 rounded-t-lg"
                    >
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg">
                        Exterior Checklist
                      </h3>

                      <ChevronDown
                        size={20}
                        className={`text-gray-600 transition-transform duration-300 ${
                          exteriorOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Content */}
                    {exteriorOpen && (
                      <div className="p-3 sm:p-4 space-y-3">
                        {Object.entries({
                          frame_tank_walkways_free: 'Frame, tank and walkways free of contamination and cargo',
                          manlid_valve_compartments_free: 'Manlid and valve compartments free of contamination and cargo',
                          serial_numbers_legible: 'Serial numbers and statutory markings legible',
                          cargo_labels_removed: 'Cargo labels removed'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`exterior_${key}`}
                              checked={formData.exterior[key]}
                              onChange={() => handleCheckboxGroupChange('exterior', key)}
                              className="w-4 h-4 accent-buttonBg"
                            />
                            <label
                              htmlFor={`exterior_${key}`}
                              className="text-xs sm:text-sm text-secondary"
                            >
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>

                  {/* Section 5: Interior Checklist */}
                  <div className="border border-gray-200 mb-4">

                    {/* Header */}
                    <button
                      type="button"
                      onClick={() => setInteriorOpen(!interiorOpen)}
                      className="w-full flex justify-between items-center px-3 py-2 sm:px-4 sm:py-3 bg-gray-50 rounded-t-lg"
                    >
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg">
                        Interior Checklist
                      </h3>

                      <ChevronDown
                        size={20}
                        className={`text-gray-600 transition-transform duration-300 ${
                          interiorOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Content */}
                    {interiorOpen && (
                      <div className="p-3 sm:p-4 space-y-3">
                        {Object.entries({
                          entry_made_by_surveyor: 'Entry made into tank by surveyor',
                          free_from_odour: 'Free from odour',
                          clean_free_from_cargo: 'Clean and free from all cargo and contamination',
                          free_from_corrosion: 'Free from corrosion or pitting',
                          dry: 'Dry'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`interior_${key}`}
                              checked={formData.interior[key]}
                              onChange={() => handleCheckboxGroupChange('interior', key)}
                              className="w-4 h-4 accent-buttonBg"
                            />
                            <label
                              htmlFor={`interior_${key}`}
                              className="text-xs sm:text-sm text-secondary"
                            >
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>

                  {/* Section 6: Valves/Fittings Checklist */}
                  <div className="border border-gray-200 mb-4">

                    {/* Header */}
                    <button
                      type="button"
                      onClick={() => setValvesOpen(!valvesOpen)}
                      className="w-full flex justify-between items-center px-3 py-2 sm:px-4 sm:py-3 bg-gray-50 rounded-t-lg"
                    >
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg">
                        Valves/Fittings (Free from contamination)
                      </h3>

                      <ChevronDown
                        size={20}
                        className={`text-gray-600 transition-transform duration-300 ${
                          valvesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Content */}
                    {valvesOpen && (
                      <div className="p-3 sm:p-4 space-y-3">
                        {Object.entries({
                          valves: 'Valves',
                          manlid_seal: 'Manlid Seal',
                          dip_pipe: 'Dip-Pipe',
                          spillage: 'Spillage',
                          gas_free_entry_permit: 'Gas Free Entry Permit Issued'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`valves_${key}`}
                              checked={formData.valves_fittings[key]}
                              onChange={() => handleCheckboxGroupChange('valves_fittings', key)}
                              className="w-4 h-4 accent-buttonBg"
                            />
                            <label
                              htmlFor={`valves_${key}`}
                              className="text-xs sm:text-sm text-secondary"
                            >
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>

                  {/* Section 7: Additional Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Additional Information</h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Additional Remarks</label>
                      <textarea
                        name="remarks_2"
                        value={formData.remarks_2}
                        onChange={handleChange}
                        rows="2"
                        placeholder="Enter additional remarks"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON 
                          <Eye
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}

                          <span className="relative z-10">{submitting ? 'Updating...' : 'Update Cleaning'}</span>
                      
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/tank-cleanings')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON 
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTankCleaning;
